import moment, { Moment } from 'moment-timezone';

import ApiAuthEvent from 'types/api/AuthEvent';
import { Locales } from 'types/locale';
import { Option } from 'types/options';

import BodyPart from 'models/BodyPart';
import BundleUser from 'models/BundleUser';
import EventProduct from 'models/EventProduct';
import Location from 'models/Location';
import { Timezone } from 'models/Market';
import Model from 'models/Model';
import User from 'models/User';

import { getFormattedDate } from 'utils/date';
import { select as selectLocale } from 'utils/locale';

export enum Statuses {
    New = 'new',
    Active = 'active',
    Done = 'done',
    Canceled = 'canceled',
    SoftCanceled = 'canceledSoft',
    HardCanceled = 'canceledHard',
}

const translationsModels = selectLocale({
    [Locales.Pl]: require('locales/models/pl.json'),
    [Locales.En]: require('locales/models/en.json'),
});

export default class AuthEvent implements Model {
    id: string;
    modelType: string;
    createdAt: Moment;
    createdAtDisplay: string;
    nurse: User;
    location?: Location;
    eventProducts: EventProduct[];
    doneStatus: Statuses;
    startAt: Moment;
    startAtDisplay: string;
    endAt: Moment;
    endAtDisplay: string;
    commentClient: string;
    isFirst: boolean;
    descriptionEndermology: string;
    descriptionEpilation: string;
    isCategoryEndermology: boolean;
    eventCanceled: string;
    canMove: boolean;
    bundleUser: BundleUser;
    productName: string;

    constructor(data: ApiAuthEvent) {
        this.id = data.id;
        this.modelType = data.modelType;
        this.createdAt = data.createdAt && moment.utc(data.createdAt).tz(Timezone);
        this.createdAtDisplay = this.createdAt && getFormattedDate(this.createdAt);
        this.nurse = data.nurse && new User(data.nurse);
        this.location = data.location && new Location(data.location);
        this.eventProducts =Array.isArray(data.eventProducts)
            ? data.eventProducts.map((eventProduct) => new EventProduct(eventProduct))
            : [];

        this.doneStatus = data.doneStatus;
        this.startAt = data.startAt && moment.utc(data.startAt).tz(Timezone);
        this.startAtDisplay = this.startAt && this.startAt.format('DD.MM.YYYY, dddd HH:mm');
        this.endAt = data.endAt && moment.utc(data.endAt).tz(Timezone);
        this.endAtDisplay = this.endAt && getFormattedDate(this.endAt);
        this.commentClient = data.commentClient;
        this.isFirst = data.isFirst;
        this.isCategoryEndermology = !!this.eventProducts.find(eventProduct => eventProduct.product?.category.name === 'epilation');
        this.descriptionEndermology = translationsModels?.authEvent?.descriptionEndermology;
        this.descriptionEpilation = translationsModels?.authEvent?.descriptionEpilation;
        this.eventCanceled = translationsModels?.authEvent?.eventCanceled;
        this.canMove = data.canMove;
        this.bundleUser = data.bundleUser && new BundleUser(data.bundleUser);
        this.productName = data.productName;
    }

    getTitle = (): string => {
        return this.bundleUser?.bundle?.locale?.name ?? this.bundleUser?.name ?? this.productName ?? this.eventProducts?.[0]?.product?.name;
    };

    getBodyParts = (): BodyPart[] => {
        const bodyParts = [];

        this.eventProducts.forEach(eventProduct => {
            const productBodyParts = eventProduct.product.productBodyParts;
            productBodyParts.forEach(productBodyPart => {
                bodyParts.push(productBodyPart.bodyPart);
            });
        });

        return bodyParts;
    };

    getOption = (): Option<AuthEvent> => {
        return {
            value: this,
            label: `${this?.startAtDisplay} | ${this.getTitle()}`,
        };
    };
}
